/**
 * TimelineSegment Component
 *
 * Renders a single segment on the timeline with:
 * - Family-based coloring (lineage colors)
 * - Selection state (bold outline)
 * - Click handling for selection
 * - Truncated labels for narrow segments
 */

import { useMemo, type CSSProperties } from 'react'
import type { TimelineSegment as TimelineSegmentType, TimelineSegmentProps } from '../types'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

/** Maximum characters before truncating label */
const SEGMENT_LABEL_MAX_CHARS = 12
/** Characters to show when truncating */
const SEGMENT_LABEL_TRUNCATE_AT = 10
/** Minimum width in pixels to show label */
const MIN_WIDTH_FOR_LABEL = 40

/**
 * Format a date for tooltip display (user-friendly)
 */
function formatDate(date: string | null, forEnd = false): string {
  if (!date) return forEnd ? '(ongoing)' : 'Immediate'
  return dayjs(date).format('MMM D, YYYY')
}

/**
 * Format a date with full timestamp precision for debugging.
 * Shows ISO format with milliseconds in UTC to help diagnose alignment issues.
 */
function formatFullTimestamp(date: string | null): string {
  if (!date) return '(null)'
  return dayjs(date).utc().format('YYYY-MM-DD HH:mm:ss.SSS') + ' UTC'
}

/**
 * Get the CSS class for a segment based on its family index
 */
function getSegmentColorClass(familyIndex: number | undefined): string {
  if (familyIndex === -1) {
    return 'timeline-segment--focus'
  }
  if (familyIndex === undefined) {
    return 'timeline-segment--background'
  }
  return `timeline-segment--family-${familyIndex}`
}

/**
 * Get the CSS class for segment type (aligned with record lifecycle states)
 */
function getSegmentTypeClass(type: TimelineSegmentType['type']): string {
  switch (type) {
    case 'new':
      return 'timeline-segment--new'
    case 'active':
      return 'timeline-segment--active'
    case 'superseded':
      return 'timeline-segment--superseded'
    case 'continuation':
      return 'timeline-segment--continuation'
    case 'expired':
      return 'timeline-segment--expired'
    default:
      return ''
  }
}

/**
 * Truncate label if too long
 */
function truncateLabel(name: string, maxChars: number = SEGMENT_LABEL_MAX_CHARS): string {
  if (name.length <= maxChars) return name
  return name.slice(0, SEGMENT_LABEL_TRUNCATE_AT) + '...'
}

/**
 * TimelineSegment Component
 */
export function TimelineSegment({
  segment,
  left,
  width,
  isSelected,
  isHovered = false,
  onClick,
  onHover,
}: TimelineSegmentProps) {
  const { name, type, familyIndex, effectiveFrom, effectiveTo, isSelectable = true } = segment

  // Build class names
  const classNames = useMemo(() => {
    const classes = ['timeline-segment', getSegmentColorClass(familyIndex), getSegmentTypeClass(type)]

    if (isSelected) {
      classes.push('timeline-segment--selected')
    }

    if (isHovered && !isSelected) {
      classes.push('timeline-segment--hovered')
    }

    if (isSelectable && onClick) {
      classes.push('timeline-segment--clickable')
    }

    return classes.filter(Boolean).join(' ')
  }, [familyIndex, type, isSelected, isHovered, isSelectable, onClick])

  // Build inline styles
  const style: CSSProperties = useMemo(() => ({
    left: `${left}px`,
    width: `${width}px`,
  }), [left, width])

  // Build tooltip - shows both friendly dates and precise timestamps for debugging
  const tooltip = useMemo(() => {
    const friendlyRange = `${formatDate(effectiveFrom)} - ${formatDate(effectiveTo, true)}`
    const preciseRange = `${formatFullTimestamp(effectiveFrom)} to ${formatFullTimestamp(effectiveTo)}`
    return `${name}\n${friendlyRange}\n[${preciseRange}]`
  }, [name, effectiveFrom, effectiveTo])

  // Determine if we should show the label
  const showLabel = width >= MIN_WIDTH_FOR_LABEL
  const displayLabel = truncateLabel(name)

  const handleClick = () => {
    if (isSelectable && onClick) {
      onClick()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && isSelectable && onClick) {
      e.preventDefault()
      onClick()
    }
  }

  const handleMouseEnter = () => {
    if (onHover) {
      onHover(segment.uid)
    }
  }

  const handleMouseLeave = () => {
    if (onHover) {
      onHover(null)
    }
  }

  return (
    <div
      className={classNames}
      style={style}
      title={tooltip}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role={isSelectable && onClick ? 'button' : undefined}
      tabIndex={isSelectable && onClick ? 0 : undefined}
      aria-pressed={isSelectable ? isSelected : undefined}
      aria-label={tooltip}
    >
      {showLabel && (
        <span className="timeline-segment__label">
          {displayLabel}
        </span>
      )}
    </div>
  )
}

export default TimelineSegment
